/**
 * dataSyncBus.js — RentKollect global data-sync layer.
 *
 * Why this exists:
 *   Every "money" page in the app (Home, Collections, Dues, P&L, Tenant Profile,
 *   Property Details, Rooms, Expenses, Bookings) reads from MongoDB. When a user
 *   records a payment on Collections, every other open page MUST reflect the new
 *   numbers — same tab AND other tabs (a manager often has Home and Dues open
 *   side-by-side). Without this bus, each page would only refresh on remount.
 *
 * How:
 *   - Local subscribers stored in a Map<event, Set<fn>>.
 *   - Cross-tab broadcast via the BroadcastChannel API (modern browsers; we
 *     gracefully degrade to in-tab only if unavailable).
 *   - Receivers debounce by 250 ms (configurable) so 10 rapid emits = 1 refetch.
 *
 * Event taxonomy (kebab/colon kept consistent):
 *   - 'payment:changed'   — new/edit/delete a payment, token-auto-log, etc.
 *   - 'tenant:changed'    — add/edit/notice/cancel-notice/exit/checkout
 *   - 'booking:changed'   — add/edit/confirm/cancel/move-in/delete
 *   - 'expense:changed'   — add/edit/delete
 *   - 'room:changed'      — add/edit/delete or status change
 *   - 'property:changed'  — add/edit/delete or bank link
 *   - 'lead:changed'      — add/edit/move-stage/delete
 *
 * Convenience constants exported below. Always import them rather than typing
 * raw strings — typos in event names won't be caught at runtime.
 */

export const EVT = Object.freeze({
  PAYMENT:  'payment:changed',
  TENANT:   'tenant:changed',
  BOOKING:  'booking:changed',
  EXPENSE:  'expense:changed',
  ROOM:     'room:changed',
  PROPERTY: 'property:changed',
  LEAD:     'lead:changed',
});

const CHANNEL_NAME = 'rentkollect-sync';
const listeners = new Map(); // event -> Set<fn>
const channel =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

const dispatchLocal = (event, payload) => {
  const set = listeners.get(event);
  if (!set) return;
  set.forEach((fn) => {
    try { fn(payload); } catch (err) {
      // Never crash other subscribers — but in dev surface the breakage so
      // it doesn't get silently ignored while iterating.
      if (process.env.NODE_ENV !== 'production') {
        try { console.debug(`[dataSyncBus:${event}]`, err?.message || err); } catch (_) {}
      }
    }
  });
};

if (channel) {
  channel.onmessage = (msg) => {
    const { event, payload } = msg.data || {};
    if (event) dispatchLocal(event, payload);
  };
}

/**
 * Broadcast an event to:
 *   1. All same-tab subscribers (synchronously).
 *   2. All other open tabs (via BroadcastChannel).
 *
 * @param {string} event   — one of EVT.* constants.
 * @param {object} payload — optional, e.g. { id, action: 'created' }.
 */
export const emit = (event, payload = {}) => {
  if (!event) return;
  dispatchLocal(event, payload);
  if (channel) {
    try { channel.postMessage({ event, payload, at: Date.now() }); }
    catch (_) { /* serialization edge cases — silent */ }
  }
};

/**
 * Subscribe to one or more events.
 * @param {string|string[]} events
 * @param {function} handler — receives the payload.
 * @returns {function} unsubscribe
 */
export const subscribe = (events, handler) => {
  const list = Array.isArray(events) ? events : [events];
  list.forEach((e) => {
    if (!listeners.has(e)) listeners.set(e, new Set());
    listeners.get(e).add(handler);
  });
  return () => {
    list.forEach((e) => listeners.get(e)?.delete(handler));
  };
};

/**
 * Convenience: emit several event types in one call (used by Move-In flow which
 * touches payments + tenants + bookings simultaneously).
 */
export const emitMany = (events, payload = {}) => {
  (Array.isArray(events) ? events : [events]).forEach((e) => emit(e, payload));
};
